import { api } from '@astro/api'

/** Load the Razorpay checkout SDK once, on demand. */
let sdk = null
function loadSdk() {
  if (window.Razorpay) return Promise.resolve()
  if (!sdk) {
    sdk = new Promise((resolve, reject) => {
      const s = document.createElement('script')
      s.src = 'https://checkout.razorpay.com/v1/checkout.js'
      s.onload = resolve
      s.onerror = () => {
        sdk = null
        reject(new Error('Could not load the payment SDK — check your connection.'))
      }
      document.body.appendChild(s)
    })
  }
  return sdk
}

/**
 * Razorpay gateway driver.
 *
 * `pay` opens the checkout popup for an order created by
 * /checkout/place-order and resolves once the popup closes (paid, failed
 * or dismissed). On payment it verifies server-side via
 * /checkout/payment/verify and reports through onSuccess/onFailure.
 */
export const razorpay = {
  /** Matches `gateway_name` in the GET /payment-gateways response. */
  key: 'razorpay',

  async pay({ order, gateway, verifyContext, sessionUser, onVerifyStart, onSuccess, onFailure }) {
    // Field fallbacks across envelope variations. The public key can also
    // come from the gateway row itself (credentials.key_id in /payment-gateways).
    const key = order?.key || order?.key_id || order?.razorpay_key || gateway?.credentials?.key_id
    const razorpayOrderId =
      order?.razorpay_order_id ||
      // Razorpay order ids are "order_…" — accept one riding in order_id/id.
      [order?.order_id, order?.id].map((v) => String(v ?? '')).find((v) => v.startsWith('order_')) ||
      null
    if (!key) {
      onFailure('Payment key missing from the order response — contact support.')
      return
    }
    if (!razorpayOrderId) {
      onFailure('Razorpay order reference missing from the order response — contact support.')
      return
    }
    await loadSdk()
    return new Promise((resolve) => {
      const rzp = new window.Razorpay({
        key,
        amount: order.amount,
        currency: order.currency || 'INR',
        name: 'Astrologer Raju Maharaj',
        order_id: razorpayOrderId,
        prefill: {
          name:
            [order.user?.first_name, order.user?.last_name].filter(Boolean).join(' ') ||
            sessionUser?.name || '',
          email: order.user?.email || sessionUser?.email || '',
          contact: order.user?.mobile_no || '',
        },
        // Site theme token --color-primary (deep maroon) — keeps the checkout
        // popup's header/buttons on-brand with the rest of the site.
        theme: { color: '#5a0a16' },
        modal: { ondismiss: () => resolve() },
        handler: async (response) => {
          // Payment captured — server-side verification begins (the caller
          // shows its processing screen from here until success/failure).
          onVerifyStart?.()
          try {
            const v = await api.checkout.verifyPayment({
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
              order_id: order.order_id ?? order.id ?? '',
              ...verifyContext,
              coupon_discount: order.coupon_discount ?? '',
              price_gst: order.price_gst ?? '',
              discounted_price: order.discounted_price ?? '',
            })
            if (v?.status) onSuccess(v)
            else onFailure(v?.message || 'Payment verification failed — contact support if you were charged.')
          } catch {
            onFailure('Payment verification error — contact support if you were charged.')
          }
          resolve()
        },
      })
      rzp.on?.('payment.failed', (r) => onFailure(r?.error?.description || 'Payment failed — try again.'))
      rzp.open()
    })
  },
}
